import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { injectIntl } from 'react-intl';
import { withTheme } from 'styled-components';
import cookie from 'js-cookie';
import URL from 'url';

import {
    CARRIER,
    ILL_CHOOSE_LATER_TEXT,
    IM_PAYING_MYSELF_TEXT,
    COOKIE_MAPPING,
    INSURANCE_TYPE,
} from '../../constants';
import InsurancePicker from '../InsurancePicker';

export const LOADING_TEXT = 'Loading...';

const getCookieMapping = insuranceType => {
    switch (insuranceType) {
        case INSURANCE_TYPE.DENTAL:
            return COOKIE_MAPPING[INSURANCE_TYPE.DENTAL];
        case INSURANCE_TYPE.VISION:
            return COOKIE_MAPPING[INSURANCE_TYPE.VISION];
        default:
            return COOKIE_MAPPING[INSURANCE_TYPE.HEALTH];
    }
};


/* _testUrl is a hack to get around jest's inability to mock window.location  */
const getInsuranceFromUrl = ({ _testUrl }) => {
    const isValid = val => parseInt(val, 10) > 0;
    const queryParams = URL.parse((_testUrl || window.location.href).toLowerCase(), true).query;

    const ret = { };
    if (isValid(queryParams.insurancecarrier) && isValid(queryParams.insuranceplan)) {
        ret.carrierId = queryParams.insurancecarrier;
        ret.planId = queryParams.insuranceplan;
    } else if (isValid(queryParams.insurance_carrier) && isValid(queryParams.insurance_plan)) {
        ret.carrierId = queryParams.insurance_carrier;
        ret.planId = queryParams.insurance_plan;
    }
    return ret;
};

const isUnselectedInsurance = ({ carrierId }) => parseInt(carrierId, 10) < 0;

const setUnselectedInsurance = ({ carrierId, chooseNonInsuranceOption, intl }) => {
    let nonInsuranceOptionName;

    if (carrierId === CARRIER.ILL_CHOOSE_LATER) {
        nonInsuranceOptionName = intl.formatMessage({
            id: 'Components.InsurancePicker.IllChooseLater',
            defaultMessage: ILL_CHOOSE_LATER_TEXT,
        });
    } else if (carrierId === CARRIER.IM_PAYING_MYSELF) {
        nonInsuranceOptionName = intl.formatMessage({
            id: 'Components.InsurancePicker.PayingMyself',
            defaultMessage: IM_PAYING_MYSELF_TEXT,
        });
    }

    chooseNonInsuranceOption({
        id: carrierId,
        name: nonInsuranceOptionName,
    }, false);
};

class InsurancePickerWrapper extends PureComponent {
    static propTypes = {
        carrierId: PropTypes.string,
        chooseCarrier: PropTypes.func,
        chooseNonInsuranceOption: PropTypes.func,
        choosePlan: PropTypes.func,
        hideValidation: PropTypes.bool,
        insuranceType: PropTypes.number,
        onChange: PropTypes.func,
        planId: PropTypes.string,
        registerCallbacks: PropTypes.func,
        setDirectoryId: PropTypes.func,
        setInsuranceType: PropTypes.func,
        ignoreDefaultCookies: PropTypes.bool,
        theme: PropTypes.shape({
            directoryId: PropTypes.string.isRequired,
        }).isRequired,
        showScanInsuranceCardButton: PropTypes.bool,
    };

    static defaultProps = {
        onChange: () => {},
        hideValidation: false,
        showScanInsuranceCardButton: false,
    };

    componentDidMount() {
        const {
            insuranceType,
            carrierId,
            planId,
            onChange,
            setInsuranceType,
            registerCallbacks,
            theme,
            setDirectoryId,
        } = this.props;

        setDirectoryId(theme.directoryId);

        setInsuranceType(insuranceType);

        if (isUnselectedInsurance(this.props)) {
            setUnselectedInsurance(this.props);
        } else if (carrierId) {
            this.setPlanAndCarrier({
                carrierId,
                planId,
            });

            this.setCookie({
                insuranceType,
                carrierId,
                planId,
            });
        } else {
            this.chooseDefault({
                insuranceType,
                checkUrl: true,
            });
        }

        registerCallbacks({
            successCallback: value => {
                onChange(value);
                this.setCookie({
                    insuranceType: value.insuranceType,
                    carrierId: value.carrier.id,
                    planId: value.plan.id,
                });
            },
            clearValuesCallback: () => {
                // To notify when insurance has been cleared (via actions' clearCarrierAndPlan)
                onChange({
                    carrier: { id: -1 },
                    plan: { id: -1 },
                });
            },
        });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.insuranceType !== this.props.insuranceType) {
            this.props.setInsuranceType(this.props.insuranceType);

            if (!this.props.carrierId) {
                this.chooseDefault({
                    insuranceType: this.props.insuranceType,
                    checkUrl: false,
                });
            }
        }
    }

    render() {
        const {
            errorStateOverride,
            gaCategory,
            hideValidation,
            insuranceAcceptanceLoading,
            insuranceIsAccepted,
            section,
            theme,
            showScanInsuranceCardButton,
            showValidationBackground,
        } = this.props;

        return (
            <InsurancePicker
                directoryId={theme.directoryId}
                errorStateOverride={errorStateOverride}
                gaCategory={gaCategory}
                hideValidation={hideValidation}
                insuranceAcceptanceLoading={insuranceAcceptanceLoading}
                insuranceIsAccepted={insuranceIsAccepted}
                section={section}
                showScanInsuranceCardButton={showScanInsuranceCardButton}
                showValidationBackground={showValidationBackground}
            />
        );
    }

    setCookie({ insuranceType, carrierId, planId }) {
        const mapping = getCookieMapping(insuranceType);
        cookie.set(mapping.carrier, carrierId);
        cookie.set(mapping.plan, planId);
    }

    setPlanAndCarrier({ carrierId, planId }) {
        const {
            chooseCarrier,
            choosePlan,
            fetchCarriersIfNeeded,
            fetchPlansIfNeeded,
            intl,
        } = this.props;

        chooseCarrier({
            id: carrierId,
            name: intl.formatMessage({
                id: 'Components.InsurancePicker.Loading',
                defaultMessage: LOADING_TEXT,
            }),
        }, false);

        if (planId && parseInt(planId, 10) > 0) {
            choosePlan({
                id: planId,
            }, null, false);
        }

        fetchCarriersIfNeeded();
        fetchPlansIfNeeded();
    }

    chooseDefault({ insuranceType, checkUrl }) {
        const mapping = getCookieMapping(insuranceType);
        const urlInsurances = checkUrl ? getInsuranceFromUrl(this.props) : { };
        const carrierId = urlInsurances.carrierId || cookie.get(mapping.carrier);
        const planId = urlInsurances.planId || cookie.get(mapping.plan);

        if (this.props.ignoreDefaultCookies) {
            this.props.chooseCarrier(null, false);
            return;
        }

        if (isUnselectedInsurance({ carrierId })) {
            setUnselectedInsurance({
                ...this.props,
                carrierId,
            });
        } else if (carrierId && planId) {
            this.setPlanAndCarrier({
                carrierId,
                planId,
            });
        } else {
            this.props.chooseCarrier(null, false);
        }

        if (urlInsurances.carrierId && urlInsurances.planId) {
            this.setCookie({
                insuranceType,
                carrierId: urlInsurances.carrierId,
                planId: urlInsurances.planId,
            });
        }
    }
}

export default withTheme(injectIntl(InsurancePickerWrapper));
